import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { CalendarDays, CalendarRange, ChevronLeft, ChevronRight, Inbox, Loader2, Plus, Repeat } from 'lucide-react';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { Avatar, PriorityBadge } from '@components/activity/ActivityBits';
import { MONTHS_PT, WEEKDAYS_PT, buildMonthGrid, isoForDay } from '@components/dateInputUtils';
import { usePermissions } from '@hooks/usePermissions';
import { useProfile } from '@contexts/ProfileContext';
import { useActivityModal } from '@contexts/ActivityModalContext';
import activityService from '@services/activityService';
import boardService from '@services/boardService';
import type { CalendarResponse } from '@/types/activity';
import type { Board } from '@/types/board';
import { BOARD_TYPE_LABELS } from '@/types/board';
import { colorClasses } from '@/utils/colors';
import { formatDateBR, monthRangeISO, todayISO } from '@/utils/formatters';
import { activityChipClasses, boardSpans, groupActivitiesByDay, shiftMonth } from './calendarUtils';

const MAX_CHIPS = 4;
const UNASSIGNED_NONE = '';

/**
 * Calendário mensal das atividades pelo prazo, com o cronograma dos eventos/projetos acima da
 * grade e o painel "Sem data" (atividades sem prazo, pendentes de priorização) ao lado.
 * Grade montada com as mesmas funções do DateInputBR (sem lib de calendário).
 */
export const CalendarView: React.FC = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-based
  const [boardId, setBoardId] = useState<string>('');
  const [assignee, setAssignee] = useState<string>(UNASSIGNED_NONE);
  const [boards, setBoards] = useState<Board[]>([]);
  const [data, setData] = useState<CalendarResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [expandedDay, setExpandedDay] = useState<string | null>(null);

  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('activities.write').hasPermission;
  const { canManageTeam, assignableMembers } = useProfile();
  const { openActivity, openNewActivity, version } = useActivityModal();

  const range = useMemo(() => monthRangeISO(year, month), [year, month]);
  const today = todayISO();

  useEffect(() => {
    boardService.getAll().then(setBoards).catch(setError);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(
        await activityService.getCalendar({
          start: range.start,
          end: range.end,
          boardId: boardId ? Number(boardId) : undefined,
          assigneeMemberId: assignee ? Number(assignee) : undefined,
        }),
      );
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [range, boardId, assignee]);

  useEffect(() => {
    load();
  }, [load, version]);

  const changeMonth = (delta: number) => {
    const next = shiftMonth(year, month, delta);
    setYear(next.year);
    setMonth(next.month);
    setExpandedDay(null);
  };

  const goToday = () => {
    setYear(now.getFullYear());
    setMonth(now.getMonth());
  };

  const byDay = useMemo(() => groupActivitiesByDay(data?.activities ?? []), [data]);
  const spans = useMemo(() => boardSpans(data?.boards ?? [], range.start, range.end), [data, range]);
  const cells = buildMonthGrid(year, month);
  const daysInMonth = cells.filter((c) => c !== null).length;
  const noDueDate = data?.no_due_date ?? [];

  return (
    <div className="space-y-4">
      {/* Cabeçalho + filtros */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => changeMonth(-1)}
            className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            aria-label="Mês anterior"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-lg font-semibold text-slate-800 min-w-[170px] text-center">
            {MONTHS_PT[month]} {year}
          </span>
          <button
            type="button"
            onClick={() => changeMonth(1)}
            className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            aria-label="Próximo mês"
          >
            <ChevronRight size={16} />
          </button>
          <button type="button" onClick={goToday} className="btn-outline py-1.5 ml-1">
            Hoje
          </button>
          {loading && <Loader2 size={16} className="animate-spin text-slate-400 ml-2" />}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56">
            <label className="text-label block mb-1">Quadro</label>
            <select className="select-field" value={boardId} onChange={(e) => setBoardId(e.target.value)}>
              <option value="">Todos os quadros</option>
              {boards.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({BOARD_TYPE_LABELS[b.board_type]})
                </option>
              ))}
            </select>
          </div>
          {canManageTeam && (
            <div className="w-52">
              <label className="text-label block mb-1">Responsável</label>
              <select className="select-field" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                <option value={UNASSIGNED_NONE}>Todos</option>
                {assignableMembers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-4">
        <div className="flex-1 min-w-0 space-y-4">
          {/* Cronograma de eventos/projetos */}
          {spans.length > 0 && (
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
                <CalendarRange size={16} className="text-slate-500" /> Cronograma
              </h3>
              <div className="overflow-x-auto">
                <div className="min-w-[640px]">
                  <div
                    className="grid gap-px mb-1"
                    style={{ gridTemplateColumns: `repeat(${daysInMonth}, minmax(0, 1fr))` }}
                  >
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const iso = isoForDay(year, month, i + 1);
                      return (
                        <div
                          key={iso}
                          className={clsx(
                            'text-center text-[10px]',
                            iso === today ? 'font-bold text-blue-600' : 'text-slate-400',
                          )}
                        >
                          {i + 1}
                        </div>
                      );
                    })}
                  </div>
                  <div
                    className="grid gap-y-1"
                    style={{ gridTemplateColumns: `repeat(${daysInMonth}, minmax(0, 1fr))` }}
                  >
                    {spans.map((s) => {
                      const cc = colorClasses(s.board.color);
                      return (
                        <Link
                          key={s.board.id}
                          to={`/kanban/${s.board.id}`}
                          title={`${s.board.name} · ${formatDateBR(s.board.start_date)} a ${formatDateBR(s.board.end_date)}`}
                          style={{ gridColumn: `${s.offset + 1} / span ${s.length}` }}
                          className={clsx(
                            'h-6 px-2 text-xs font-medium flex items-center truncate hover:opacity-80 border',
                            cc.bar,
                            cc.textStrong,
                            cc.border,
                            s.clippedStart ? 'rounded-l-none' : 'rounded-l-md',
                            s.clippedEnd ? 'rounded-r-none' : 'rounded-r-md',
                          )}
                        >
                          {s.board.name}
                          <span className="ml-1 opacity-70">· {s.board.open_count} abertas</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Grade do mês */}
          <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-xl overflow-hidden border border-slate-200">
            {WEEKDAYS_PT.map((wd) => (
              <div key={wd} className="bg-slate-50 text-center text-xs font-medium text-slate-500 py-2">
                {wd}
              </div>
            ))}
            {cells.map((day, idx) => {
              if (day === null) return <div key={`empty-${idx}`} className="bg-slate-50/60 min-h-[110px]" />;
              const iso = isoForDay(year, month, day);
              const items = byDay[iso] ?? [];
              const isToday = iso === today;
              const expanded = expandedDay === iso;
              const visible = expanded ? items : items.slice(0, MAX_CHIPS);
              return (
                <div
                  key={iso}
                  className={clsx(
                    'group bg-white min-h-[110px] p-1.5 flex flex-col',
                    isToday && 'ring-2 ring-inset ring-blue-400',
                  )}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={clsx('text-xs', isToday ? 'font-bold text-blue-600' : 'text-slate-500')}>{day}</span>
                    {canWrite && (
                      <button
                        type="button"
                        onClick={() =>
                          openNewActivity({ due_date: iso, board_id: boardId ? Number(boardId) : undefined })
                        }
                        className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 transition-opacity"
                        title={`Nova atividade para ${formatDateBR(iso)}`}
                      >
                        <Plus size={12} />
                      </button>
                    )}
                  </div>
                  <div className="space-y-0.5 flex-1">
                    {visible.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => openActivity(a.id)}
                        title={`${a.title} · ${a.board_name}${a.assignee_name ? ` · ${a.assignee_name}` : ''}`}
                        className={clsx(
                          'w-full text-left truncate rounded px-1 py-0.5 text-[11px] leading-tight hover:opacity-80',
                          activityChipClasses(a),
                        )}
                      >
                        {a.recurrence_rule_id && <Repeat size={9} className="inline mr-0.5 -mt-0.5" />}
                        {a.title}
                      </button>
                    ))}
                    {items.length > MAX_CHIPS && (
                      <button
                        type="button"
                        onClick={() => setExpandedDay(expanded ? null : iso)}
                        className="text-[11px] text-slate-500 hover:text-slate-800 px-1"
                      >
                        {expanded ? 'mostrar menos' : `+${items.length - MAX_CHIPS} mais`}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-3 text-xs text-slate-600">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded bg-rose-100 border border-rose-300" /> Crítica
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded bg-amber-50 border border-amber-300" /> Alta
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded bg-sky-50 border border-sky-200" /> Média
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded bg-slate-100 border border-slate-200" /> Baixa
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded bg-red-100 border border-red-300" /> Atrasada
            </span>
            <span className="inline-flex items-center gap-1.5 line-through text-slate-400">Concluída</span>
          </div>
        </div>

        {/* Painel "Sem data" */}
        <aside className="xl:w-80 flex-shrink-0">
          <div className="card p-4 xl:sticky xl:top-0">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Inbox size={16} className="text-violet-600" /> Sem data
              <span className="badge-neutral">{noDueDate.length}</span>
            </h3>
            <p className="text-small mt-1 mb-3">Pendentes de priorização — clique para definir o prazo.</p>
            {noDueDate.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6 flex flex-col items-center gap-2">
                <CalendarDays size={24} className="text-slate-300" />
                Todas as atividades têm prazo.
              </p>
            ) : (
              <div className="space-y-1.5 max-h-[60vh] overflow-y-auto custom-scrollbar">
                {noDueDate.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => openActivity(a.id)}
                    className="w-full text-left p-2 rounded-lg border border-slate-200 hover:border-violet-300 hover:bg-violet-50 transition-colors"
                  >
                    <p className="text-sm text-slate-800 line-clamp-2">{a.title}</p>
                    <div className="flex items-center justify-between mt-1.5 gap-2">
                      <span className="text-[11px] text-slate-400 truncate">{a.board_name}</span>
                      <div className="flex items-center gap-1.5">
                        <PriorityBadge priority={a.priority} />
                        <Avatar name={a.assignee_name} />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
